import * as React from 'react';

import {
  Col,
  IconButton,
  Row,
  Switch,
  Toggle,
  ToggleButtonGroup,
  Tooltip,
  withColor,
} from '@rematter/ui-web';
import { useAtom } from 'jotai';
import {
  LucideAlertCircle,
  LucideAlertTriangle,
  LucideLoader2,
  LucideRefreshCw,
} from 'lucide-react';

import {
  $colorMode,
  $enabledLayers,
  MODULE_SOURCES,
} from '../_state/preferences.state';
import { TRUNCATION_LIMIT } from '../hooks/useCalendarEvents';
import { useTranslations } from '../i18n/useTranslations';
import {
  ALL_MODULES,
  type CalendarEventSource,
  type CalendarModule,
  type ModuleState,
} from '../types/calendarEvent';
import type { ColorMode } from '../types/colorConfig';
import {
  DEFAULT_ENTITY_TYPE_COLORS,
  DEFAULT_MODULE_COLORS,
} from '../utils/colorDefaults';

const COLOR_MODE_OPTIONS: ColorMode[] = [
  'byModule',
  'byEntityType',
  'byStatus',
];

interface CalendarSidebarProps {
  moduleStates?: Record<string, ModuleState>;
  onRetry?: (module: CalendarModule) => void;
}

export const CalendarSidebar = ({
  moduleStates,
  onRetry,
}: CalendarSidebarProps) => {
  const t = useTranslations();
  const [enabledLayers, setEnabledLayers] = useAtom($enabledLayers);
  const [colorMode, setColorMode] = useAtom($colorMode);

  const handleSourceToggle = React.useCallback(
    (source: CalendarEventSource, enabled: boolean) => {
      setEnabledLayers({ ...enabledLayers, [source]: enabled });
    },
    [enabledLayers, setEnabledLayers],
  );

  const handleModuleToggle = React.useCallback(
    (module: CalendarModule, enabled: boolean) => {
      const sources = MODULE_SOURCES[module];
      const update = { ...enabledLayers };
      for (const source of sources) {
        update[source] = enabled;
      }
      setEnabledLayers(update);
    },
    [enabledLayers, setEnabledLayers],
  );

  const handleColorModeChange = React.useCallback(
    (keys: Set<React.Key>) => {
      const first = [...keys][0];
      if (first) {
        setColorMode(first as ColorMode);
      }
    },
    [setColorMode],
  );

  return (
    <Col
      className="@CalendarSidebar h-full overflow-y-auto border-r border-neutral/10 p-4"
      gap4
    >
      <Col gap2>
        <span className="text-xs font-semibold tracking-wider text-neutral/50 uppercase">
          {t.sidebar.layers}
        </span>
        <Col gap1>
          {ALL_MODULES.map((module) => {
            const sources = MODULE_SOURCES[module];
            const allEnabled = sources.every((s) => enabledLayers[s]);
            const someEnabled = sources.some((s) => enabledLayers[s]);
            const state = moduleStates?.[module];

            return (
              <Col key={module}>
                <Row yCenter className="justify-between py-1">
                  <Row yCenter gap2>
                    <div
                      className="size-2.5 shrink-0 rounded-full bg-custom-color"
                      style={withColor(DEFAULT_MODULE_COLORS[module])}
                    />
                    <span className="text-sm font-medium">
                      {t.sidebar.modules[module]}
                    </span>
                    {someEnabled && state?.loading && (
                      <LucideLoader2 className="size-3.5 animate-spin text-neutral/40" />
                    )}
                    {someEnabled && state?.error && (
                      <Tooltip tip={t.sidebar.moduleError}>
                        <Row yCenter gap2>
                          <LucideAlertCircle className="size-3.5 text-failure" />
                          {onRetry && (
                            <IconButton
                              icon={<LucideRefreshCw className="size-3" />}
                              variant="ghost"
                              aria-label={t.sidebar.retry}
                              onPress={() => onRetry(module)}
                            />
                          )}
                        </Row>
                      </Tooltip>
                    )}
                  </Row>
                  <Switch
                    isSelected={allEnabled}
                    onChange={(enabled) => handleModuleToggle(module, enabled)}
                  />
                </Row>
                {someEnabled && sources.length > 1 && (
                  <Col className="ml-5 border-l border-neutral/10 pl-3">
                    {sources.map((source) => (
                      <Row
                        key={source}
                        yCenter
                        className="justify-between py-0.5"
                      >
                        <Row yCenter gap2>
                          <div
                            className="size-2 shrink-0 rounded-full bg-custom-color"
                            style={withColor(
                              DEFAULT_ENTITY_TYPE_COLORS[source],
                            )}
                          />
                          <span className="text-xs text-neutral/70">
                            {t.sources[source]}
                          </span>
                        </Row>
                        <Switch
                          isSelected={enabledLayers[source] ?? false}
                          onChange={(enabled) =>
                            handleSourceToggle(source, enabled)
                          }
                        />
                      </Row>
                    ))}
                  </Col>
                )}
              </Col>
            );
          })}
        </Col>
      </Col>

      <Col gap2>
        <span className="text-xs font-semibold tracking-wider text-neutral/50 uppercase">
          {t.sidebar.colorMode}
        </span>
        <ToggleButtonGroup
          selectionMode="single"
          selectedKeys={new Set([colorMode])}
          onSelectionChange={handleColorModeChange}
          orientation="vertical"
        >
          {COLOR_MODE_OPTIONS.map((mode) => (
            <Toggle key={mode} id={mode} size="sm">
              {t.sidebar.colorModes[mode]}
            </Toggle>
          ))}
        </ToggleButtonGroup>
      </Col>

      <ModuleStatusSection
        enabledLayers={enabledLayers}
        moduleStates={moduleStates}
      />
    </Col>
  );
};

type ModuleStatusSectionProps = {
  enabledLayers: Record<string, boolean>;
  moduleStates?: Record<string, ModuleState>;
};

const ModuleStatusSection = ({
  enabledLayers,
  moduleStates,
}: ModuleStatusSectionProps) => {
  const t = useTranslations();

  const enabledModulesWithState = React.useMemo(
    () =>
      ALL_MODULES.filter((m) => {
        const sources = MODULE_SOURCES[m];
        const someEnabled = sources.some((s) => enabledLayers[s]);
        return someEnabled && moduleStates?.[m];
      }),
    [enabledLayers, moduleStates],
  );

  if (enabledModulesWithState.length === 0) return null;

  const hasAnyStatus = enabledModulesWithState.some((m) => {
    const state = moduleStates?.[m];
    return state && (state.loading || state.error || state.truncated);
  });

  if (!hasAnyStatus) return null;

  return (
    <Col gap2 className="@ModuleStatusSection">
      <span className="text-xs font-semibold tracking-wider text-neutral/50 uppercase">
        {t.sidebar.status}
      </span>
      <Col gap1>
        {enabledModulesWithState.map((module) => {
          const state = moduleStates?.[module];
          if (!state || (!state.loading && !state.error && !state.truncated))
            return null;

          return (
            <Row key={module} yCenter gap2 className="text-xs">
              <span className="font-medium">{t.sidebar.modules[module]}</span>
              {state.loading && (
                <LucideLoader2 className="size-3 animate-spin text-neutral/40" />
              )}
              {state.error && (
                <LucideAlertCircle className="size-3 text-failure" />
              )}
              {state.truncated && !state.error && (
                <Row yCenter gap1>
                  <LucideAlertTriangle className="size-3 text-warning" />
                  <span className="text-warning">
                    {TRUNCATION_LIMIT} of {state.totalCount ?? 'N'}+
                  </span>
                </Row>
              )}
            </Row>
          );
        })}
      </Col>
    </Col>
  );
};
